import React from "react";
import paths from "@icarius-router/paths";
import CommonPage from "@icarius-common/commonPage";
import { useSelector } from "react-redux";
import { getIsAdmin } from "@icarius-app/selectors";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import { getLocalizedString } from "@icarius-localization/strings";
import useCatalogs from "./useCatalogs";
import ListDialog from "./listDialog";
import EnableTableDialog from "./enableTableDialog";
import QueryDialog from "./queryDialog";
import CreateModifyTableDialog from "./createModifyTableDialog";
import CreateModifyFieldDialog from "./createModifyFieldDialog";
import AssignOptionsDialog from "./assignOptionsDialog";

const Catalogs = ({ history }) => {
    const { isLoading, data, fieldsData, state, handlers, typeList, subTypeList } = useCatalogs();

    const isAdmin = useSelector(getIsAdmin);

    const structureGoBackItem = () => (
        <CustomIconButton
            title={getLocalizedString("goBack")}
            onClick={handlers.handleClearSelectedRow}
            type={"goBack"}
        />
    );

    const goBackItem = () => (
        <CustomIconButton
            title={getLocalizedString("goBack")}
            onClick={() => history.push(paths.extensibilityScreenSelector)}
            type={"goBack"}
        />
    );

    const enableTableItem = (gridRef) =>
        isAdmin && (
            <CustomIconButton
                title={getLocalizedString("enableEditorsForSystemTable")}
                onClick={() => handlers.handleOpenEnableTableDialog(gridRef)}
                type={"checklist"}
            />
        );

    const createTable = (gridRef) =>
        isAdmin && (
            <CustomIconButton
                title={getLocalizedString("createTable")}
                onClick={() => handlers.handleOpenCreateTableDialog(gridRef)}
                type={"add"}
            />
        );

    const modifyTable = (gridRef) =>
        isAdmin && (
            <CustomIconButton
                title={getLocalizedString("modifyTable")}
                onClick={() => handlers.handleOpenModifyTableDialog(gridRef)}
                type={"edit"}
            />
        );

    const deleteTable = (gridRef) =>
        isAdmin && (
            <CustomIconButton
                title={getLocalizedString("deleteTable")}
                onClick={() => handlers.handleOpenDeleteTableDialog(gridRef)}
                type={"delete"}
            />
        );

    const createField = (gridRef) =>
        isAdmin && (
            <CustomIconButton
                title={getLocalizedString("createField")}
                onClick={() => handlers.handleOpenCreateFieldDialog(gridRef)}
                type={"add"}
            />
        );

    const modifyField = (gridRef) =>
        isAdmin && (
            <CustomIconButton
                title={getLocalizedString("modifyField")}
                onClick={() => handlers.handleOpenModifyFieldDialog(gridRef)}
                type={"edit"}
            />
        );

    const deleteField = (gridRef) =>
        isAdmin && (
            <CustomIconButton
                title={getLocalizedString("deleteField")}
                onClick={() => handlers.handleOpenDeleteFieldDialog(gridRef)}
                type={"delete"}
            />
        );

    const validValues = (gridRef) =>
        isAdmin && (
            <CustomIconButton
                title={getLocalizedString("validValuesList")}
                onClick={() => handlers.handleOpenModifyFieldOptionsDialog(gridRef)}
                type={"list"}
            />
        );

    const copyQueryItem = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("generateFieldNamesForQueries")}
            onClick={() => handlers.handleOpenQueryDialog(gridRef)}
            type={"duplicate"}
        />
    );

    if (state.selectedRow) {
        const title = `${getLocalizedString("fieldStructureTable")}: ${state.selectedRow.code} - ${
            state.selectedRow.name
        }`;

        return (
            <CommonPage
                isLoading={isLoading}
                title={title}
                gridTitle={title}
                columnDefPage={paths.catalogFields}
                rowData={fieldsData}
                handleRowClick={handlers.handleCatalogItemRowClick}
                menuItems={[structureGoBackItem, createField, modifyField, deleteField, copyQueryItem, validValues]}
                hasExpand
                hasHelp
                ignoreProcessesbyPath
            >
                {Boolean(state.selectedRowOptionList?.length) && (
                    <ListDialog
                        open={Boolean(state.selectedRowOptionList?.length)}
                        data={state.selectedRowOptionList}
                        handleClose={handlers.handleCloseListDialog}
                    />
                )}
                {Boolean(state.queryText?.length) && (
                    <QueryDialog
                        open={Boolean(state.queryText?.length)}
                        data={state.queryText}
                        handleClose={handlers.handleCloseQueryDialog}
                    />
                )}
                {state.createModifyFieldDialogIsOpen && (
                    <CreateModifyFieldDialog
                        typeList={typeList}
                        subTypeList={subTypeList}
                        open={state.createModifyFieldDialogIsOpen}
                        isLoading={isLoading}
                        data={state.createModifyFieldDialogData}
                        handleClose={handlers.handleCloseCreateModifyFieldDialog}
                        selectedRow={state.selectedRow}
                    />
                )}
                {state.modifyFieldOptionsDialogIsOpen && (
                    <AssignOptionsDialog
                        typeList={typeList}
                        subTypeList={subTypeList}
                        open={state.modifyFieldOptionsDialogIsOpen}
                        isLoading={isLoading}
                        data={state.modifyFieldOptionsDialogData}
                        handleClose={handlers.handleCloseModifyFieldOptionsDialog}
                    />
                )}
            </CommonPage>
        );
    }

    return (
        <CommonPage
            isLoading={isLoading}
            title={getLocalizedString("catalogTablesList")}
            gridTitle={getLocalizedString("catalogTablesList")}
            columnDefPage={paths.catalogs}
            rowData={data}
            handleRowClick={handlers.handleCatalogRowClick}
            menuItems={[goBackItem, createTable, modifyTable, deleteTable, enableTableItem]}
            hasExpand
            hasHelp
        >
            {Boolean(state.enableTableDialogData) && (
                <EnableTableDialog
                    open={Boolean(state.enableTableDialogData)}
                    isLoading={isLoading}
                    data={state.enableTableDialogData}
                    handleClose={handlers.handleCloseEnableTableDialog}
                />
            )}
            {state.createModifyTableDialogIsOpen && (
                <CreateModifyTableDialog
                    open={state.createModifyTableDialogIsOpen}
                    isLoading={isLoading}
                    data={state.createModifyTableDialogData}
                    handleClose={handlers.handleCloseCreateModifyTableDialog}
                />
            )}
        </CommonPage>
    );
};

export default Catalogs;
